import React, { useState, useEffect } from 'react'
import { XMarkIcon, MagnifyingGlassIcon, ArrowDownTrayIcon, LinkIcon } from '@heroicons/react/24/outline'

const IconSearchModal = ({ isOpen, onClose, onSelectIcon }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedCollection, setSelectedCollection] = useState('all')
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadingIcon, setDownloadingIcon] = useState(null)

  // 中文关键词映射表
  const chineseKeywordMap = {
    // 基础图标
    '首页': ['home', 'house', 'main'],
    '主页': ['home', 'house', 'main'],
    '用户': ['user', 'person', 'profile', 'account'],
    '设置': ['settings', 'config', 'gear', 'cog'],
    '搜索': ['search', 'find', 'magnify'],
    '邮件': ['email', 'mail', 'message', 'envelope'],
    '电话': ['phone', 'call', 'telephone'],
    '地址': ['location', 'map', 'place', 'pin'],
    '时间': ['time', 'clock', 'watch'],
    '日期': ['date', 'calendar', 'schedule'],
    '文件': ['file', 'document', 'folder'],
    '图片': ['image', 'photo', 'picture'],
    '视频': ['video', 'play', 'movie'],
    '音乐': ['music', 'audio', 'sound'],
    '下载': ['download', 'save'],
    '上传': ['upload', 'cloud'],
    '删除': ['delete', 'trash', 'remove'],
    '编辑': ['edit', 'pencil', 'write'],
    '保存': ['save', 'disk', 'floppy'],
    '打印': ['print', 'printer'],
    '分享': ['share', 'social'],
    '喜欢': ['like', 'heart', 'favorite'],
    '星星': ['star', 'favorite'],
    '购物': ['shopping', 'cart', 'bag'],
    '钱': ['money', 'dollar', 'coin'],
    '安全': ['security', 'lock', 'shield'],
    '警告': ['warning', 'alert', 'danger'],
    '信息': ['info', 'information'],
    '帮助': ['help', 'question', 'support'],
    '关闭': ['close', 'x', 'cancel'],
    '菜单': ['menu', 'hamburger', 'bars'],
    '更多': ['more', 'dots', 'ellipsis'],
    '返回': ['back', 'arrow', 'return'],
    '前进': ['forward', 'next', 'arrow'],
    '上': ['up', 'arrow'],
    '下': ['down', 'arrow'],
    '左': ['left', 'arrow'],
    '右': ['right', 'arrow'],

    // 社交媒体
    '微信': ['wechat', 'weixin'],
    '微博': ['weibo'],
    '抖音': ['tiktok', 'douyin'],
    '支付宝': ['alipay'],
    '淘宝': ['taobao'],
    '京东': ['jd'],
    '百度': ['baidu'],
    '腾讯': ['tencent', 'qq'],

    // 技术相关
    '代码': ['code', 'programming', 'dev'],
    '编程': ['programming', 'code', 'dev'],
    '开发': ['development', 'dev', 'code'],
    '数据库': ['database', 'data', 'storage'],
    '服务器': ['server', 'cloud', 'hosting'],
    '网络': ['network', 'wifi', 'internet'],
    '安卓': ['android'],
    '苹果': ['apple', 'ios'],
    '电脑': ['computer', 'desktop', 'pc'],
    '手机': ['mobile', 'phone', 'smartphone'],
    '平板': ['tablet', 'ipad'],

    // 商业相关
    '公司': ['company', 'business', 'office'],
    '办公': ['office', 'work', 'business'],
    '会议': ['meeting', 'conference'],
    '报告': ['report', 'chart', 'analytics'],
    '统计': ['statistics', 'chart', 'graph'],
    '销售': ['sales', 'money', 'chart'],
    '客户': ['customer', 'user', 'client'],

    // 生活相关
    '食物': ['food', 'restaurant', 'eat'],
    '饮料': ['drink', 'coffee', 'tea'],
    '交通': ['transport', 'car', 'bus', 'train'],
    '旅行': ['travel', 'plane', 'vacation'],
    '运动': ['sport', 'fitness', 'gym'],
    '健康': ['health', 'medical', 'heart'],
    '教育': ['education', 'school', 'book'],
    '游戏': ['game', 'play', 'controller'],
    '天气': ['weather', 'sun', 'cloud', 'rain']
  }

  // 图标集合选项 - 优先显示彩色图标集
  const iconCollections = [
    { id: 'all', name: '全部', prefix: '' },
    { id: 'noto', name: 'Noto Emoji (彩色)', prefix: 'noto' },
    { id: 'twemoji', name: 'Twitter Emoji (彩色)', prefix: 'twemoji' },
    { id: 'emojione', name: 'EmojiOne (彩色)', prefix: 'emojione' },
    { id: 'fluent-emoji', name: 'Fluent Emoji (彩色)', prefix: 'fluent-emoji' },
    { id: 'logos', name: 'Brand Logos (彩色)', prefix: 'logos' },
    { id: 'simple-icons', name: 'Simple Icons (品牌)', prefix: 'simple-icons' },
    { id: 'devicon', name: 'Devicon (开发工具)', prefix: 'devicon' },
    { id: 'skill-icons', name: 'Skill Icons (技能)', prefix: 'skill-icons' },
    { id: 'vscode-icons', name: 'VS Code Icons', prefix: 'vscode-icons' },
    { id: 'file-icons', name: 'File Icons', prefix: 'file-icons' },
    { id: 'mdi', name: 'Material Design Icons', prefix: 'mdi' },
    { id: 'heroicons', name: 'Heroicons', prefix: 'heroicons' },
    { id: 'lucide', name: 'Lucide', prefix: 'lucide' },
    { id: 'tabler', name: 'Tabler Icons', prefix: 'tabler' },
    { id: 'carbon', name: 'Carbon Icons', prefix: 'carbon' },
    { id: 'phosphor', name: 'Phosphor Icons', prefix: 'phosphor' },
    { id: 'feather', name: 'Feather Icons', prefix: 'feather' },
    { id: 'ant-design', name: 'Ant Design Icons', prefix: 'ant-design' },
    { id: 'bootstrap', name: 'Bootstrap Icons', prefix: 'bi' }
  ]

  // 智能关键词处理函数
  const processSearchKeywords = (query) => {
    const keywords = new Set()
    const originalQuery = query.trim().toLowerCase()

    // 添加原始查询
    keywords.add(originalQuery)

    // 处理中文关键词映射
    for (const [chinese, englishWords] of Object.entries(chineseKeywordMap)) {
      if (originalQuery.includes(chinese)) {
        englishWords.forEach(word => keywords.add(word))
      }
    }

    // 处理英文关键词的同义词和相关词
    const englishSynonyms = {
      'home': ['house', 'main', 'dashboard'],
      'user': ['person', 'profile', 'account', 'avatar'],
      'settings': ['config', 'gear', 'cog', 'preferences'],
      'search': ['find', 'magnify', 'lookup'],
      'email': ['mail', 'message', 'envelope', 'letter'],
      'phone': ['call', 'telephone', 'mobile'],
      'location': ['map', 'place', 'pin', 'marker'],
      'time': ['clock', 'watch', 'schedule'],
      'file': ['document', 'folder', 'doc'],
      'image': ['photo', 'picture', 'gallery'],
      'video': ['play', 'movie', 'film'],
      'music': ['audio', 'sound', 'song'],
      'download': ['save', 'import'],
      'upload': ['cloud', 'export'],
      'delete': ['trash', 'remove', 'bin'],
      'edit': ['pencil', 'write', 'modify'],
      'share': ['social', 'send'],
      'like': ['heart', 'favorite', 'love'],
      'star': ['favorite', 'bookmark'],
      'shopping': ['cart', 'bag', 'store'],
      'money': ['dollar', 'coin', 'payment'],
      'security': ['lock', 'shield', 'safe'],
      'warning': ['alert', 'danger', 'caution'],
      'info': ['information', 'details'],
      'help': ['question', 'support', 'faq'],
      'close': ['x', 'cancel', 'exit'],
      'menu': ['hamburger', 'bars', 'navigation'],
      'arrow': ['direction', 'pointer', 'chevron'],
      'code': ['programming', 'dev', 'developer'],
      'database': ['data', 'storage', 'server'],
      'network': ['wifi', 'internet', 'connection'],
      'mobile': ['phone', 'smartphone', 'device'],
      'computer': ['desktop', 'pc', 'laptop'],
      'business': ['office', 'work', 'company'],
      'chart': ['graph', 'analytics', 'statistics'],
      'food': ['restaurant', 'eat', 'dining'],
      'transport': ['car', 'bus', 'train', 'vehicle'],
      'travel': ['plane', 'vacation', 'trip'],
      'health': ['medical', 'doctor', 'hospital'],
      'education': ['school', 'book', 'learn'],
      'game': ['play', 'controller', 'entertainment'],
      'weather': ['sun', 'cloud', 'rain', 'climate']
    }

    // 添加同义词
    for (const keyword of Array.from(keywords)) {
      if (englishSynonyms[keyword]) {
        englishSynonyms[keyword].forEach(synonym => keywords.add(synonym))
      }
    }

    return Array.from(keywords)
  }

  // 增强的搜索图标函数
  const searchIcons = async (query, collection = 'all') => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      // 处理搜索关键词，获取所有相关的英文关键词
      const searchKeywords = processSearchKeywords(query)
      console.log('搜索关键词:', searchKeywords)

      let allResults = []
      const resultMap = new Map() // 用于去重

      if (collection === 'all') {
        // 扩展的彩色图标集合
        const priorityCollections = [
          'noto', 'twemoji', 'emojione', 'fluent-emoji', 'fluent-emoji-flat',
          'logos', 'simple-icons', 'devicon', 'devicon-plain',
          'skill-icons', 'vscode-icons', 'file-icons', 'cryptocurrency',
          'flag', 'circle-flags', 'flagpack', 'openmoji'
        ]

        // 对每个关键词进行搜索
        for (const keyword of searchKeywords.slice(0, 5)) { // 限制关键词数量避免过多请求
          // 先搜索优先级高的彩色图标集
          for (const prefix of priorityCollections) {
            if (allResults.length >= 200) break // 限制总数量

            try {
              const searchUrl = `https://api.iconify.design/search?query=${encodeURIComponent(keyword)}&prefix=${prefix}&limit=15`
              const response = await fetch(searchUrl)
              const data = await response.json()

              if (data.icons && Array.isArray(data.icons)) {
                data.icons.forEach(icon => {
                  if (!resultMap.has(icon)) {
                    resultMap.set(icon, true)
                    allResults.push(icon)
                  }
                })
              }
            } catch (error) {
              console.warn(`搜索 ${prefix} 图标集失败:`, error)
            }
          }

          // 如果结果不够，搜索通用图标集
          if (allResults.length < 100) {
            try {
              const searchUrl = `https://api.iconify.design/search?query=${encodeURIComponent(keyword)}&limit=30`
              const response = await fetch(searchUrl)
              const data = await response.json()

              if (data.icons && Array.isArray(data.icons)) {
                data.icons.forEach(icon => {
                  if (!resultMap.has(icon)) {
                    resultMap.set(icon, true)
                    allResults.push(icon)
                  }
                })
              }
            } catch (error) {
              console.warn(`搜索关键词 ${keyword} 失败:`, error)
            }
          }
        }

        // 如果还是结果不够，尝试模糊搜索
        if (allResults.length < 50) {
          const fuzzyKeywords = [
            query.trim().toLowerCase(),
            ...query.trim().toLowerCase().split('').map((_, i, arr) =>
              arr.slice(0, i).join('') + '*' + arr.slice(i + 1).join('')
            ).filter(k => k.length > 2)
          ]

          for (const fuzzyKeyword of fuzzyKeywords.slice(0, 3)) {
            try {
              const searchUrl = `https://api.iconify.design/search?query=${encodeURIComponent(fuzzyKeyword)}&limit=20`
              const response = await fetch(searchUrl)
              const data = await response.json()

              if (data.icons && Array.isArray(data.icons)) {
                data.icons.forEach(icon => {
                  if (!resultMap.has(icon)) {
                    resultMap.set(icon, true)
                    allResults.push(icon)
                  }
                })
              }
            } catch (error) {
              console.warn(`模糊搜索 ${fuzzyKeyword} 失败:`, error)
            }
          }
        }
      } else {
        // 搜索特定图标集
        const selectedCol = iconCollections.find(col => col.id === collection)
        if (selectedCol && selectedCol.prefix) {
          for (const keyword of searchKeywords.slice(0, 3)) {
            try {
              const searchUrl = `https://api.iconify.design/search?query=${encodeURIComponent(keyword)}&prefix=${selectedCol.prefix}&limit=50`
              const response = await fetch(searchUrl)
              const data = await response.json()

              if (data.icons && Array.isArray(data.icons)) {
                data.icons.forEach(icon => {
                  if (!resultMap.has(icon)) {
                    resultMap.set(icon, true)
                    allResults.push(icon)
                  }
                })
              }
            } catch (error) {
              console.warn(`搜索特定图标集失败:`, error)
            }
          }
        }
      }

      // 对结果进行智能排序
      const sortedResults = allResults.sort((a, b) => {
        const aLower = a.toLowerCase()
        const bLower = b.toLowerCase()
        const queryLower = query.toLowerCase()

        // 优先显示完全匹配的
        if (aLower.includes(queryLower) && !bLower.includes(queryLower)) return -1
        if (!aLower.includes(queryLower) && bLower.includes(queryLower)) return 1

        // 优先显示彩色图标集
        const colorfulPrefixes = ['noto', 'twemoji', 'emojione', 'fluent-emoji', 'logos', 'simple-icons']
        const aIsColorful = colorfulPrefixes.some(prefix => a.startsWith(prefix + ':'))
        const bIsColorful = colorfulPrefixes.some(prefix => b.startsWith(prefix + ':'))

        if (aIsColorful && !bIsColorful) return -1
        if (!aIsColorful && bIsColorful) return 1

        return 0
      })

      setSearchResults(sortedResults.slice(0, 150)) // 增加到150个图标
    } catch (error) {
      console.error('搜索图标失败:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // 防抖搜索
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        if (searchQuery.trim()) {
          searchIcons(searchQuery, selectedCollection)
        } else {
          setSearchResults([])
        }
      } catch (error) {
        console.error('搜索过程中发生错误:', error)
        setSearchResults([])
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, selectedCollection])

  // 下载图标到本地
  const downloadIcon = async (iconName) => {
    setIsDownloading(true)
    setDownloadingIcon(iconName)

    try {
      // 获取图标SVG数据
      const response = await fetch(`https://api.iconify.design/${iconName}.svg`)
      const svgData = await response.text()

      // 发送到后端保存
      const saveResponse = await fetch('/api/icons/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          iconName: iconName.replace(':', '_'), // 替换冒号为下划线
          svgData: svgData
        })
      })

      if (saveResponse.ok) {
        const result = await saveResponse.json()
        // 返回完整的 URL，让 Homepage 能够访问 Config Manager 的图标
        // 使用当前页面的 origin 来构建完整 URL
        const iconUrl = `${window.location.origin}/icons/${result.fileName}`
        onSelectIcon(iconUrl)
        onClose()
      } else {
        throw new Error('保存图标失败')
      }
    } catch (error) {
      console.error('下载图标失败:', error)
      alert('下载图标失败，请重试')
    } finally {
      setIsDownloading(false)
      setDownloadingIcon(null)
    }
  }

  // 直接使用在线图标
  const useOnlineIcon = (iconName) => {
    const iconUrl = `https://api.iconify.design/${iconName}.svg`
    onSelectIcon(iconUrl)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 标题栏 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">搜索图标</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 搜索区域 */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex gap-4 mb-4">
            {/* 搜索输入框 */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none z-10" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索图标... (例如: home, user, settings)"
                className="w-full px-4 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoComplete="off"
                autoFocus
              />
            </div>

            {/* 图标集合选择 */}
            <select
              value={selectedCollection}
              onChange={(e) => setSelectedCollection(e.target.value)}
              className="min-w-[200px] px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {iconCollections.map(collection => (
                <option key={collection.id} value={collection.id} className="bg-gray-700 text-white">
                  {collection.name}
                </option>
              ))}
            </select>
          </div>

          <p className="text-sm text-gray-400">
            搜索来自 Iconify 的数十万个图标，支持下载到本地或直接使用在线链接
          </p>

          {/* 热门搜索建议 */}
          {!searchQuery && (
            <div className="mt-4">
              <p className="text-sm text-gray-400 mb-2">热门搜索：</p>
              <div className="flex flex-wrap gap-2">
                {[
                  // 中文常用词
                  '🏠 首页', '👤 用户', '⚙️ 设置', '📧 邮件', '🔒 安全',
                  '❤️ 喜欢', '⭐ 星星', '🔍 搜索', '📱 手机', '💻 电脑',
                  '📁 文件', '🎵 音乐', '🎬 视频', '📷 图片', '🛒 购物',
                  '💰 钱', '🌐 网络', '🎮 游戏', '📚 教育', '🏥 健康',
                  // 英文常用词
                  '🌟 brand', '🎨 design', '🚀 rocket', '📊 chart', '🔔 notification',
                  '🔧 tools', '📈 analytics', '🎯 target', '💡 idea', '🔥 fire',
                  // 技术相关
                  '💻 code', '🗄️ database', '☁️ cloud', '🔗 api', '🛡️ security',
                  // 社交媒体
                  '📱 wechat', '🐦 twitter', '📘 facebook', '📸 instagram', '🎵 tiktok'
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      const keyword = suggestion.split(' ')[1]
                      setSearchQuery(keyword)
                    }}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-full text-xs text-gray-300 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 搜索结果 */}
        <div className="flex-1 overflow-auto p-6">
          {/* 搜索结果统计 */}
          {searchQuery && !isSearching && (
            <div className="mb-4 text-sm text-gray-400">
              找到 <span className="text-primary-400 font-medium">{searchResults.length}</span> 个图标
              {searchResults.length >= 150 && <span className="ml-2 text-yellow-400">（显示前150个结果）</span>}
            </div>
          )}

          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-400">智能搜索中...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="grid grid-cols-10 sm:grid-cols-12 md:grid-cols-15 lg:grid-cols-18 xl:grid-cols-20 2xl:grid-cols-24 gap-2">
              {searchResults.map((iconName, index) => {
                // 安全检查图标名称
                if (!iconName || typeof iconName !== 'string') {
                  return null
                }

                return (
                  <div
                    key={`${iconName}-${index}`}
                    className="group relative bg-gray-700 rounded-lg p-2 hover:bg-gray-600 transition-colors"
                  >
                    {/* 图标预览 */}
                    <div className="aspect-square flex items-center justify-center mb-1">
                      <img
                        src={`https://api.iconify.design/${iconName}.svg`}
                        alt={iconName}
                        className="w-6 h-6"
                        onError={(e) => {
                          // 如果图标加载失败，尝试不同的尺寸
                          e.target.src = `https://api.iconify.design/${iconName}.svg?width=24&height=24`
                        }}
                      />
                    </div>

                    {/* 图标名称 */}
                    <p className="text-xs text-gray-300 text-center truncate" title={iconName}>
                      {iconName.includes(':') ? iconName.split(':')[1] : iconName}
                    </p>

                  {/* 操作按钮 */}
                  <div className="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <div className="flex gap-1">
                      <button
                        onClick={() => downloadIcon(iconName)}
                        disabled={isDownloading}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                        title="下载到本地"
                      >
                        {downloadingIcon === iconName ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-white"></div>
                        ) : (
                          <ArrowDownTrayIcon className="h-3 w-3" />
                        )}
                        下载
                      </button>
                      <button
                        onClick={() => useOnlineIcon(iconName)}
                        className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                        title="使用在线链接"
                      >
                        <LinkIcon className="h-3 w-3" />
                        在线
                      </button>
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          ) : searchQuery ? (
            <div className="text-center py-12">
              <p className="text-gray-400">未找到相关图标</p>
              <p className="text-sm text-gray-500 mt-2">尝试使用不同的关键词搜索</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <MagnifyingGlassIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">输入关键词开始搜索图标</p>
              <p className="text-sm text-gray-500 mt-2">支持中文和英文搜索</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default IconSearchModal
